import { useEffect, useState } from "react";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { ExpensesTable, cardLabel } from "@/components/ExpensesTable";
import { DateRangeFilter, type DateRange } from "@/components/ExpenseUi";
import { SpenderFilter, type SpenderScope } from "@/components/SpenderFilter";
import { ExpenseSearchBox, ExpenseSearchResults } from "@/components/ExpenseSearch";
import { useDolfinClient } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-public";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Tabs are a work queue: what's stuck, what awaits approval (the review gate is on by default), the receipt chase, then all.

type TabValue = "attention" | "to-approve" | "missing-receipts" | "all";
type CardConnectionResponse = components["schemas"]["CardConnectionResponse"];

const ALL_CARDS = "all";
const TABS: readonly TabValue[] = ["attention", "to-approve", "missing-receipts", "all"];
const isTab = (value?: string): value is TabValue => TABS.includes(value as TabValue);

export interface ExpensesPageProps {
  onSelect?: (expenseId: string) => void;
  /** Open already filtered to one card — e.g. from that card's "View spend" link. */
  initialCardConnectionId?: string;
  /** Open on one tab: "attention", "to-approve", "missing-receipts" or "all". Anything else is ignored. */
  initialTab?: string;
  /** The search to show. Pass with onSearchChange to keep it somewhere that survives navigation, like the URL. */
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export function ExpensesPage({
  onSelect,
  initialCardConnectionId,
  initialTab,
  searchQuery,
  onSearchChange,
}: ExpensesPageProps = {}) {
  const api = useDolfinClient();
  // Controlled when the host keeps the query (the URL), otherwise held here.
  const [ownQuery, setOwnQuery] = useState("");
  const query = searchQuery ?? ownQuery;
  const setQuery = onSearchChange ?? setOwnQuery;
  // Arriving for one card means "show me what this card spent", which is the All spend tab — the work
  // queue would hide most of it.
  const [tab, setTab] = useState<TabValue>(
    isTab(initialTab) ? initialTab : initialCardConnectionId ? "all" : "attention",
  );
  const [cardId, setCardId] = useState<string>(
    initialCardConnectionId ?? ALL_CARDS,
  );
  const [cards, setCards] = useState<CardConnectionResponse[]>([]);
  const [dates, setDates] = useState<DateRange>({});
  const [whose, setWhose] = useState<SpenderScope>({ kind: "everyone" });
  const mine = whose.kind === "mine";
  const person = whose.kind === "person" ? whose : undefined;

  useEffect(() => {
    api
      .GET("/v1/card-connections", {
        params: { query: { IncludeRemoved: true } },
      })
      .then(({ data }) => setCards(data ?? []));
  }, [api]);

  const cardFilter = cardId === ALL_CARDS ? undefined : cardId;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Card spend"
        subtitle="Every card transaction is captured and coded automatically. Upload a receipt to sharpen the VAT position and split the coding."
      />

      <ExpenseSearchBox query={query} onSearch={setQuery} />

      {/* A search answers its own question, so the work-queue tabs and their filters step aside for it. */}
      {query ? (
        <div>
          <ExpenseSearchResults
            query={query}
            onSelect={onSelect}
            onClear={() => setQuery("")}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
              <TabsList>
                <TabsTrigger value="attention">Needs attention</TabsTrigger>
                <TabsTrigger value="to-approve">To approve</TabsTrigger>
                <TabsTrigger value="missing-receipts">
                  Missing receipts
                </TabsTrigger>
                <TabsTrigger value="all">All spend</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap items-center gap-2">
              <SpenderFilter value={whose} onChange={setWhose} />
              <DateRangeFilter value={dates} onChange={setDates} />
              {/* Only worth showing once there is more than one card to tell apart. */}
              {cards.length > 1 ? (
                <Select value={cardId} onValueChange={setCardId}>
                  <SelectTrigger
                    className="h-9 w-52"
                    aria-label="Filter by card"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_CARDS}>All cards</SelectItem>
                    {cards.map((card) => (
                      <SelectItem key={card.id} value={card.id!}>
                        {card.displayName ? `${card.displayName} · ` : ""}
                        {cardLabel({
                          cardNetwork: card.network,
                          cardLast4: card.last4,
                        })}
                        {card.status === "Removed" ? " (removed)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>
          </div>

          <div className="pt-4">
            {tab === "attention" ? (
              <ExpensesTable
                flagged
                cardConnectionId={cardFilter}
                mine={mine}
                spenderUserId={person?.userId}
                spenderLabel={person?.label}
                {...dates}
                onSelect={onSelect}
              />
            ) : tab === "to-approve" ? (
              <ExpensesTable
                state="PendingReview"
                cardConnectionId={cardFilter}
                mine={mine}
                spenderUserId={person?.userId}
                spenderLabel={person?.label}
                {...dates}
                onSelect={onSelect}
              />
            ) : tab === "missing-receipts" ? (
              <ExpensesTable
                needsReceipt
                cardConnectionId={cardFilter}
                mine={mine}
                spenderUserId={person?.userId}
                spenderLabel={person?.label}
                {...dates}
                onSelect={onSelect}
              />
            ) : (
              <ExpensesTable
                cardConnectionId={cardFilter}
                mine={mine}
                spenderUserId={person?.userId}
                spenderLabel={person?.label}
                {...dates}
                onSelect={onSelect}
              />
            )}
          </div>
        </>
      )}
    </PageContainer>
  );
}
